import React, { useState, useEffect } from 'react';
import {
  Alert,
  AlertTitle,
  Snackbar,
  Button,
  Box,
  Typography,
  Collapse
} from '@mui/material';
import {
  WifiOff as OfflineIcon,
  Wifi as OnlineIcon,
  Block as SuspendedIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionService, SUBSCRIPTION_STATUS } from '../services/subscriptionService';
import logger from '../utils/logger';

/**
 * Offline / Suspended Mode Banner (B.4)
 *
 * Displays a persistent banner when:
 *   1. The browser is offline (navigator.onLine === false)
 *   2. The family's subscription is suspended or past due
 *
 * In offline mode: read-only access, writes are queued (future Firestore persistence)
 * In suspended mode: read-only access, prompts upgrade/payment
 */
const OfflineBanner = () => {
  const { currentUser } = useAuth();
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showReconnected, setShowReconnected] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  const userId = currentUser?.uid;

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setShowReconnected(true);
    };

    const handleOffline = () => {
      setIsOffline(true);
      setShowReconnected(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check subscription status (per-user, per LAUNCH_ROADMAP.md)
  useEffect(() => {
    const checkSubscription = async () => {
      if (!userId || isOffline) return;

      try {
        const sub = await subscriptionService.getSubscription(userId);
        setSubscriptionStatus(sub?.status || SUBSCRIPTION_STATUS.ACTIVE);
      } catch (err) {
        logger.error('Error checking subscription status:', err);
      }
    };

    checkSubscription();
    // Recheck every 5 minutes
    const interval = setInterval(checkSubscription, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [userId, isOffline]);

  const isSuspended = subscriptionStatus === SUBSCRIPTION_STATUS.SUSPENDED;
  const isPastDue = subscriptionStatus === SUBSCRIPTION_STATUS.PAST_DUE;

  return (
    <>
      {/* Offline Banner */}
      <Collapse in={isOffline}>
        <Alert
          severity="warning"
          icon={<OfflineIcon />}
          sx={{
            borderRadius: 0,
            '& .MuiAlert-message': { width: '100%' }
          }}
        >
          <AlertTitle>You are offline</AlertTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">
              Some features may be unavailable. Changes will be saved when you reconnect.
            </Typography>
          </Box>
        </Alert>
      </Collapse>

      {/* Suspended Subscription Banner */}
      <Collapse in={isSuspended && !isOffline}>
        <Alert
          severity="error"
          icon={<SuspendedIcon />}
          sx={{
            borderRadius: 0,
            '& .MuiAlert-message': { width: '100%' }
          }}
          action={
            <Button
              color="inherit"
              size="small"
              href="/settings"
            >
              Update Payment
            </Button>
          }
        >
          <AlertTitle>Account Suspended</AlertTitle>
          <Typography variant="body2">
            Your subscription has been suspended due to a payment issue.
            You have read-only access. Please update your payment method to restore full access.
          </Typography>
        </Alert>
      </Collapse>

      {/* Past Due Warning */}
      <Collapse in={isPastDue && !isSuspended && !isOffline}>
        <Alert
          severity="warning"
          sx={{
            borderRadius: 0,
            '& .MuiAlert-message': { width: '100%' }
          }}
          action={
            <Button
              color="inherit"
              size="small"
              href="/settings"
            >
              Update Payment
            </Button>
          }
        >
          <Typography variant="body2">
            Your payment is past due. Please update your payment method to avoid service interruption.
          </Typography>
        </Alert>
      </Collapse>

      {/* Reconnected Snackbar */}
      <Snackbar
        open={showReconnected}
        autoHideDuration={3000}
        onClose={() => setShowReconnected(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowReconnected(false)}
          severity="success"
          icon={<OnlineIcon />}
          sx={{ width: '100%' }}
        >
          Back online! All changes have been synced.
        </Alert>
      </Snackbar>
    </>
  );
};

export default OfflineBanner;
